import { useCallback, useMemo, useState } from 'react'
import { GlobalHotKeys } from 'react-hotkeys'
import { IoSearch } from 'react-icons/io5'
import { cn } from '~/@/lib/utils'
import useNavigationLoading from '~/common/hooks/useNavigationLoading'
import { useTableLoading } from '~/common/hooks/useTableLoadingContext'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { controlKey, globalKeyMap } from '~/common/utils/keymap'
import Placeholder, { PlaceholderType } from '../shared/Placeholder'
import { SearchInputModal } from './SearchInputModal'

interface SearchInputProps {
  isMobile?: boolean
  shouldShowGlobalLoading?: boolean
}

export type SearchModalOpenType = 'click' | 'keyboard'

/** Top level search bar input */
export const SearchInput = ({ isMobile = false, shouldShowGlobalLoading }: SearchInputProps) => {
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false)
  const [searchModalOpenType, setSearchModalOpenType] = useState<SearchModalOpenType>('keyboard')
  const navigationIsLoading = useNavigationLoading()
  const { tableLoading } = useTableLoading()
  const showGlobalLoading = (tableLoading || navigationIsLoading) && shouldShowGlobalLoading
  const handleCommandK = useCallback(() => {
    setSearchModalOpenType('keyboard')
    setIsSearchModalOpen(true)
  }, [])

  // Sets a mapping between cmd+k and ctrl+k to set focus on the search bar
  const handlers = useMemo(
    () => ({
      COMMAND_K: handleCommandK,
      CONTROL_K: handleCommandK,
    }),
    [handleCommandK],
  )

  return (
    <>
      <div
        className="group cursor-pointer"
        onClick={() => {
          setSearchModalOpenType('click')
          setIsSearchModalOpen((curr) => !curr)
        }}
      >
        {/* Sets up hotkeys on the provided keymap using the handlers */}
        <GlobalHotKeys allowChanges handlers={handlers} keyMap={globalKeyMap}>
          <div
            className={cn(
              'flex w-full items-center justify-between rounded bg-gray-2 px-3 py-2 text-gray-10 hover:bg-gray-3',
              isMobile &&
                'rounded-md border border-gray-4 bg-gray-3 p-1 hover:border-gray-5 hover:bg-gray-3',
            )}
          >
            <IconContainer isMobile={isMobile} showGlobalLoading={showGlobalLoading} />
            {!isMobile && (
              <div className="inline-flex items-center justify-center text-xs">
                {controlKey('K')}
              </div>
            )}
          </div>
        </GlobalHotKeys>
      </div>
      <SearchInputModal
        onClose={() => {
          setIsSearchModalOpen(false)
        }}
        open={isSearchModalOpen}
        openType={searchModalOpenType}
      />
    </>
  )
}

type IconContainerProps = {
  showGlobalLoading?: boolean
  isMobile?: boolean
} & React.HTMLAttributes<HTMLDivElement>

const IconContainer = ({ showGlobalLoading, isMobile, ...props }: IconContainerProps) => {
  const { t } = useTypedTranslation()

  return (
    <div
      className={cn(
        'flex min-w-0 flex-1 items-center gap-2',
        isMobile ? 'h-6 w-full justify-start text-sm text-gray-9' : 'text-sm',
      )}
      {...props}
    >
      <div
        className={cn(
          'flex shrink-0 items-center justify-center',
          isMobile ? 'ms-2 size-4' : 'size-5',
        )}
      >
        {showGlobalLoading ? (
          <Placeholder type={PlaceholderType.NAVIGATION} />
        ) : (
          <IoSearch size={isMobile ? 14 : 18} />
        )}
      </div>
      <span className="min-w-0 truncate text-sm">{t('breadcrumbs.search')}</span>
    </div>
  )
}
